// F-MOTOR-RESERVAS.3.0.4 — Voucher com identidade oficial Rede Hospeda.
// Paleta e logo via src/lib/pdfBrand.js. Nenhuma alteração em lógica, valores ou dados.
import { jsPDF } from "jspdf";
import { FISCAL } from "./fiscal";
import { PDF_BRAND, loadBrandLogoDataUrl, loadBrandLogoDarkDataUrl } from "./pdfBrand";

function iso2br(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function brl(n) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));
}

export async function generateVoucherPDF(r) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();

  const { AZUL, AZUL_PROFUNDO, DOURADO, CREME, INK, MUTED, DIVIDER, FONT_TITLE, FONT_BODY, FONT_MONO } = PDF_BRAND;

  const logoDataUrl = await loadBrandLogoDarkDataUrl();

  // Header band oficial (azul primário)
  doc.setFillColor(...AZUL);
  doc.rect(0, 0, W, 140, "F");

  // Logo oficial à esquerda (altura 60pt, ratio ~2.56:1 → width ~154pt)
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", 40, 36, 150, 59);
    } catch (err) {
      // fallback silencioso para o wordmark em texto
      _drawWordmarkFallback(doc);
    }
  } else {
    _drawWordmarkFallback(doc);
  }

  doc.setTextColor(...CREME);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(10);
  doc.text("Voucher de Reserva  |  Gestão de Pousadas", 40, 118);

  // Contato / endereço da pousada à direita
  const rightX = W - 40;
  doc.setFont(FONT_BODY, "bold");
  doc.setFontSize(11);
  doc.text(r.pousada_nome || "—", rightX, 48, { align: "right" });
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(9);
  const enderecoLines = doc.splitTextToSize(r.pousada_endereco || "", 300);
  let ry = 64;
  enderecoLines.slice(0, 2).forEach((line) => {
    doc.text(line, rightX, ry, { align: "right" });
    ry += 12;
  });
  if (r.pousada_telefone) {
    doc.text(`Tel: ${r.pousada_telefone}`, rightX, ry, { align: "right" });
    ry += 12;
  }
  if (r.pousada_email) {
    doc.text(r.pousada_email, rightX, ry, { align: "right" });
  }

  // Accent bar oficial (dourado)
  doc.setFillColor(...DOURADO);
  doc.rect(0, 140, W, 6, "F");

  // Código da reserva
  doc.setTextColor(...MUTED);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(9);
  doc.text("CÓDIGO DA RESERVA", 48, 180);
  doc.setTextColor(...INK);
  doc.setFont(FONT_MONO, "normal");
  doc.setFontSize(11);
  doc.text(String(r.id || "").slice(0, 8).toUpperCase(), 48, 198);

  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("STATUS", W - 200, 180);
  doc.setFontSize(12);
  doc.setTextColor(...AZUL);
  doc.setFont(FONT_TITLE, "bold");
  doc.text((r.status || "ativa").toUpperCase(), W - 200, 198);

  // Divisor
  doc.setDrawColor(...DIVIDER);
  doc.line(48, 220, W - 48, 220);

  // Hóspede
  doc.setFont(FONT_TITLE, "bold");
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  doc.text(r.hospede_nome || "Hóspede", 48, 255);

  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const guestInfo = [
    r.hospede_documento ? `Documento: ${r.hospede_documento}` : null,
    r.hospede_telefone ? `Telefone: ${r.hospede_telefone}` : null,
  ].filter(Boolean).join("   •   ");
  if (guestInfo) doc.text(guestInfo, 48, 275);

  // Painel da estadia (fundo creme oficial)
  const panelY = 300;
  doc.setFillColor(...CREME);
  doc.setDrawColor(...DIVIDER);
  doc.rect(48, panelY, W - 96, 150, "FD");

  const col1 = 68;
  const col2 = 260;
  const col3 = 430;

  const labelCard = (x, y, label, value, big = false, maxWidth = null) => {
    doc.setFont(FONT_BODY, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label.toUpperCase(), x, y);
    doc.setFont(FONT_TITLE, "bold");
    const baseSize = big ? 18 : 14;
    doc.setFontSize(baseSize);
    doc.setTextColor(...INK);
    const txt = String(value ?? "—");
    // F-FIX.VOUCHER.7.1 — evita overflow do campo Quarto quando nome é longo.
    if (maxWidth) {
      // reduz fonte dinamicamente até caber em maxWidth OU quebra em até 2 linhas.
      let size = baseSize;
      while (size > 9 && doc.getTextWidth(txt) > maxWidth) {
        size -= 1;
        doc.setFontSize(size);
      }
      if (doc.getTextWidth(txt) > maxWidth) {
        const lines = doc.splitTextToSize(txt, maxWidth).slice(0, 2);
        doc.text(lines, x, y + 22);
      } else {
        doc.text(txt, x, y + 22);
      }
    } else {
      doc.text(txt, x, y + 22);
    }
  };

  // Larguras das colunas do painel: col2 (Quarto) tem até col3 - col2 - 12pt de respiro.
  const col1W = col2 - col1 - 12;
  const col2W = col3 - col2 - 12;
  const col3W = (W - 48) - col3 - 12;

  labelCard(col1, panelY + 28, "Pousada", r.pousada_nome, false, col1W);
  labelCard(col2, panelY + 28, "Quarto", `Nº ${r.quarto_numero}${r.quarto_tipo ? ` · ${r.quarto_tipo}` : ""}`, false, col2W);
  labelCard(col3, panelY + 28, "Diárias", r.diarias, true, col3W);

  labelCard(col1, panelY + 90, "Check-in", iso2br(r.data_entrada), false, col1W);
  labelCard(col2, panelY + 90, "Check-out", iso2br(r.data_saida), false, col2W);
  labelCard(col3, panelY + 90, "Valor/diária", brl(r.valor_diaria), false, col3W);

  // Total (fundo azul profundo oficial)
  const totalY = 480;
  doc.setFillColor(...AZUL_PROFUNDO);
  doc.rect(48, totalY, W - 96, 70, "F");
  // Faixa dourada lateral à esquerda
  doc.setFillColor(...DOURADO);
  doc.rect(48, totalY, 6, 70, "F");

  doc.setTextColor(...CREME);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(10);
  doc.text("VALOR TOTAL DA ESTADIA", 72, totalY + 28);
  doc.setFont(FONT_TITLE, "bold");
  doc.setFontSize(28);
  doc.text(brl(r.valor_total), 72, totalY + 58);

  if (r.tarifa_nome) {
    doc.setFont(FONT_BODY, "normal");
    doc.setFontSize(9);
    doc.setTextColor(...DOURADO);
    doc.text(`Tarifa aplicada: ${r.tarifa_nome}`, W - 260, totalY + 58);
  }

  // Observações
  if (r.observacoes) {
    doc.setTextColor(...MUTED);
    doc.setFont(FONT_BODY, "normal");
    doc.setFontSize(9);
    doc.text("OBSERVAÇÕES", 48, 580);
    doc.setTextColor(...INK);
    doc.setFontSize(10);
    const wrapped = doc.splitTextToSize(r.observacoes, W - 96);
    doc.text(wrapped, 48, 598);
  }

  // Rodapé
  doc.setDrawColor(...DIVIDER);
  doc.line(48, 750, W - 48, 750);
  doc.setTextColor(...MUTED);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(9);
  doc.text(r.pousada_nome || "Rede Hospeda", 48, 770);
  const footerRight = [r.pousada_telefone && `Tel: ${r.pousada_telefone}`, r.pousada_email]
    .filter(Boolean).join("   ·   ");
  if (footerRight) doc.text(footerRight, W - 48, 770, { align: "right" });
  doc.setFontSize(8);
  doc.text(`${FISCAL.razaoSocial} — CNPJ ${FISCAL.cnpj}`, 48, 786);
  doc.text(`Emitido em ${new Date().toLocaleString("pt-BR")}`, W - 48, 786, { align: "right" });

  const filename = `voucher-${(r.hospede_nome || "reserva").replace(/\s+/g, "_")}-${r.data_entrada}.pdf`;
  doc.save(filename);
}

// Fallback de wordmark caso o logo oficial não carregue.
function _drawWordmarkFallback(doc) {
  doc.setTextColor(...PDF_BRAND.CREME);
  doc.setFont(PDF_BRAND.FONT_TITLE, "bold");
  doc.setFontSize(26);
  doc.text("REDE HOSPEDA", 40, 78);
}
